import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowRight, RotateCw, ExternalLink, CircleDot, X, Check, Minus, Shield, ShieldAlert, ShieldCheck, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import ExplainProrateButton from './ExplainProrateButton';
import ExplainRenewalButton from './ExplainRenewalButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_LABEL = {
  admin_ui: { label: 'Admin', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  razorpay_webhook: { label: 'Payment', className: 'bg-green-100 text-green-700 border-green-200' },
  auto_expiry: { label: 'Auto-expiry', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  migration: { label: 'Migration', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  system: { label: 'System', className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const CAT_LABEL = {
  plan_change: 'Plan change',
  role_change: 'Role change',
  capability_change: 'Capabilities',
  user_edit: 'Info edited',
  password_reset: 'Password reset',
  twofa_reset: '2FA reset',
  user_delete: 'Account action',
};

const CAT_DOT = {
  plan_change: 'text-emerald-500',
  role_change: 'text-slate-500',
  capability_change: 'text-purple-500',
  user_edit: 'text-blue-500',
  password_reset: 'text-orange-500',
  twofa_reset: 'text-rose-500',
  user_delete: 'text-red-500',
};

const formatTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const groupByDay = (events) => {
  const groups = {};
  for (const e of events) {
    const day = (e.timestamp || '').slice(0, 10) || 'Unknown';
    (groups[day] ||= []).push(e);
  }
  return Object.entries(groups); // preserves insertion order (newest first)
};

// Kept in sync with backend/routes/user_audit.py::MODULE_KEYS. Labels are UI-only.
const MODULE_LABELS = {
  practice_sessions: 'Practice',
  progress_tracker: 'Progress Tracker',
  notebook: 'Notebook',
  exam_insights: 'Exam Insights',
  live_classes: 'Live Classes',
};

const PLAN_STATE_META = {
  active: { label: 'Active', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  archived: { label: 'Archived', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  deleted: { label: 'Deleted', className: 'bg-rose-50 text-rose-700 border-rose-200' },
  no_plan: { label: 'No plan', className: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const PlanAccessInline = ({ planAccess }) => {
  if (!planAccess) return null;

  const {
    plan_id: planId,
    plan_name: planName,
    plan_state: planState,
    plan_grade: planGrade,
    plan_price: planPrice,
    plan_duration_days: planDuration,
    override_active: overrideActive,
    role_bypass: roleBypass,
    modules = [],
  } = planAccess;

  const stateMeta = PLAN_STATE_META[planState] || PLAN_STATE_META.no_plan;

  // Only surface a warning chip when there's ACTUAL divergence between
  // what the plan grants and what the user effectively has. Keeps the
  // drawer quiet for the 95% of users where plan == effective.
  let anomaly = null;
  if (roleBypass) {
    anomaly = { icon: ShieldCheck, className: 'bg-purple-50 text-purple-700 border-purple-200',
                text: 'Staff bypass — all modules' };
  } else if (overrideActive) {
    anomaly = { icon: Shield, className: 'bg-indigo-50 text-indigo-700 border-indigo-200',
                text: 'Admin override — all modules' };
  } else if (planState === 'archived') {
    anomaly = { icon: ShieldAlert, className: 'bg-amber-50 text-amber-700 border-amber-200',
                text: 'Plan archived' };
  } else if (planState === 'deleted') {
    anomaly = { icon: ShieldAlert, className: 'bg-rose-50 text-rose-700 border-rose-200',
                text: 'Plan deleted' };
  }

  return (
    <div
      className="flex items-center flex-wrap gap-x-2 gap-y-1 py-2 border-b text-[11px]"
      data-testid="user-activity-plan-inline"
    >
      <span className="text-slate-500 font-medium">Plan</span>
      <span className="text-slate-900 font-medium" data-testid="plan-inline-name">
        {planName || (planId ? planId : 'None')}
      </span>
      <span
        className={`inline-block px-1.5 py-0 rounded-full border ${stateMeta.className}`}
        data-testid="plan-inline-state"
      >
        {stateMeta.label}
      </span>
      {anomaly && (
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0 rounded-full border ${anomaly.className}`}
          data-testid="plan-inline-anomaly"
        >
          <anomaly.icon className="w-3 h-3" />
          {anomaly.text}
        </span>
      )}

      <Popover>
        <PopoverTrigger
          className="ml-auto inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
          data-testid="plan-modules-trigger"
        >
          Show modules
          <ChevronDown className="w-3 h-3" />
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 text-xs p-3" data-testid="plan-modules-content">
          <div className="mb-2">
            <div className="font-medium text-sm text-slate-900">{planName || 'No plan attached'}</div>
            <div className="text-[11px] text-slate-500">
              {[planGrade, planPrice != null ? `₹${planPrice}` : null,
                planDuration ? `${planDuration}d` : null].filter(Boolean).join(' · ') || 'Legacy / free tier'}
            </div>
          </div>
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-2 py-1 font-medium">Module</th>
                  <th className="text-center px-2 py-1 font-medium w-16">Plan</th>
                  <th className="text-center px-2 py-1 font-medium w-20">Effective</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => {
                  const diverges = m.plan !== m.effective;
                  return (
                    <tr key={m.key} className="border-t border-slate-100" data-testid={`plan-modules-row-${m.key}`}>
                      <td className="px-2 py-1 text-slate-700">
                        {MODULE_LABELS[m.key] || m.key}
                      </td>
                      <td className="text-center px-2 py-1">
                        {m.plan ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600 inline" />
                        ) : (
                          <Minus className="w-3.5 h-3.5 text-slate-300 inline" />
                        )}
                      </td>
                      <td className={`text-center px-2 py-1 ${diverges ? 'bg-amber-50' : ''}`}>
                        {m.effective ? (
                          <Check className={`w-3.5 h-3.5 inline ${diverges ? 'text-amber-600' : 'text-emerald-600'}`} />
                        ) : (
                          <Minus className="w-3.5 h-3.5 text-slate-300 inline" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            Live from Finances → Plans. Reflects current plan config, not the config at purchase time.
          </p>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const EventBody = ({ evt }) => {
  const d = evt.details || {};
  switch (evt.category) {
    case 'plan_change':
      return (
        <div className="text-sm">
          <div className="font-medium">
            {d.from_plan || '—'} <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
            <span className="text-emerald-700">{d.to_plan || '—'}</span>
          </div>
          {d.to_expires_at && (
            <div className="text-xs text-slate-500">Expires {formatTs(d.to_expires_at)}</div>
          )}
          {(d.amount_paid || d.order_id || d.payment_id) && (
            <div className="text-xs text-slate-500 mt-1">
              {d.amount_paid ? `₹${d.amount_paid}` : ''}
              {d.coupon_code ? ` · Coupon ${d.coupon_code}` : ''}
              {d.coins_redeemed ? ` · ${d.coins_redeemed} coins${d.coins_discount ? ` (₹${d.coins_discount} off)` : ''}` : ''}
              {d.payment_id ? ` · Txn ${d.payment_id}` : ''}
              {d.order_id ? ` · Order ${d.order_id}` : ''}
            </div>
          )}
          {d.note && <div className="text-xs italic text-slate-500 mt-1">{d.note}</div>}
          {d.prorate_meta && (
            <div className="mt-1">
              <ExplainProrateButton meta={d.prorate_meta} />
            </div>
          )}
          {!d.prorate_meta && d.renewal_meta && (
            <div className="mt-1">
              <ExplainRenewalButton meta={d.renewal_meta} />
            </div>
          )}
        </div>
      );
    case 'role_change':
      return (
        <div className="text-sm font-medium">
          {d.from_role} <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
          <span className="text-emerald-700">{d.to_role}</span>
        </div>
      );
    case 'capability_change':
      return (
        <div className="text-xs">
          {d.added?.length ? <div className="text-emerald-700">+ {d.added.join(', ')}</div> : null}
          {d.removed?.length ? <div className="text-rose-600">− {d.removed.join(', ')}</div> : null}
          {d.to_perms?.length ? (
            <div className="text-slate-500 mt-1">Now: {d.to_perms.join(', ')}</div>
          ) : (
            <div className="text-slate-400 italic mt-1">No capabilities</div>
          )}
        </div>
      );
    case 'user_edit':
      return (
        <div className="text-xs text-slate-700 space-y-0.5">
          {Object.entries(d.changed_fields || {}).map(([k, v]) => (
            <div key={k}><span className="font-medium">{k}:</span> {String(v.from ?? '—')} → {String(v.to ?? '—')}</div>
          ))}
        </div>
      );
    case 'user_delete':
      return (
        <div className="text-sm">
          {d.kind === 'restore' ? 'Account restored' : d.kind === 'permanent' ? 'Permanently deleted' : `Soft-deleted · Blacklisted ${d.blacklist_duration || '—'} days`}
          {d.reason ? <div className="text-xs text-slate-500 mt-1">{d.reason}</div> : null}
        </div>
      );
    default:
      return <div className="text-xs text-slate-500">—</div>;
  }
};

const UserActivityDrawer = ({ open, onClose, targetUserId, targetUserName, allowJumpToFullLog = true, onJumpToFullLog }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // In-memory filters — the endpoint already returns up to 100 events for
  // this user in one shot, so filtering client-side keeps the drawer snappy
  // and avoids a refetch cycle on every chip click.
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchHistory = useCallback(async () => {
    if (!targetUserId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/user-audit-events/user/${targetUserId}?limit=100`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load user activity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    if (open && targetUserId) fetchHistory();
    if (!open) {
      setData(null);
      // Reset filters so re-opening the drawer for a different user is clean.
      setSelectedCats(new Set());
      setFromDate('');
      setToDate('');
    }
  }, [open, targetUserId, fetchHistory]);

  const target = data?.target;
  const events = data?.events || [];
  const counts = data?.counts || {};
  const planAccess = data?.plan_access;

  const toggleCat = (cat) => {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const filteredEvents = useMemo(() => {
    if (!events.length) return events;
    // Timezone-safe day boundaries: fromDate treated as start-of-day local,
    // toDate treated as end-of-day local, both compared against the event's
    // ISO timestamp.
    const fromMs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toMs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : null;
    return events.filter((e) => {
      if (selectedCats.size && !selectedCats.has(e.category)) return false;
      if (fromMs || toMs) {
        const t = e.timestamp ? new Date(e.timestamp).getTime() : NaN;
        if (Number.isNaN(t)) return false;
        if (fromMs && t < fromMs) return false;
        if (toMs && t > toMs) return false;
      }
      return true;
    });
  }, [events, selectedCats, fromDate, toDate]);

  const hasActiveFilter = selectedCats.size > 0 || fromDate || toDate;
  const clearFilters = () => {
    setSelectedCats(new Set());
    setFromDate('');
    setToDate('');
  };

  const dayGroups = groupByDay(filteredEvents);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="user-activity-drawer">
        <SheetHeader className="pb-3 border-b">
          <SheetTitle className="text-xl">
            {target?.name || targetUserName || 'User activity'}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {target?.indos_number || ''} {target?.email ? `· ${target.email}` : ''}
            {target?.role ? <span className="ml-2 inline-block"><Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] py-0">{target.role}</Badge></span> : null}
          </SheetDescription>
        </SheetHeader>

        {/* Plan access snapshot — live from Finances → Plans. Slim inline
            header + Popover to match the "Explain this change" pattern used
            elsewhere in the drawer. */}
        {data && !loading && planAccess && (
          <PlanAccessInline planAccess={planAccess} />
        )}

        {/* Summary chips — clickable to filter to that category */}
        {data && !loading && (
          <div className="flex flex-wrap gap-1.5 py-3 border-b" data-testid="user-activity-summary">
            {Object.entries(counts).map(([cat, n]) => {
              const active = selectedCats.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCat(cat)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] transition ${
                    active
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                  }`}
                  data-testid={`user-activity-chip-${cat}`}
                  aria-pressed={active}
                >
                  <CircleDot className={`w-3 h-3 ${active ? 'text-white' : (CAT_DOT[cat] || 'text-slate-400')}`} />
                  {CAT_LABEL[cat] || cat} · {n}
                </button>
              );
            })}
            {Object.keys(counts).length === 0 && (
              <span className="text-xs text-slate-500 italic">No admin actions on record for this user yet.</span>
            )}
          </div>
        )}

        {/* Date range filter */}
        {data && !loading && events.length > 0 && (
          <div className="flex items-center gap-2 py-2 border-b text-[11px]" data-testid="user-activity-date-filter">
            <span className="text-slate-500 font-medium">Range</span>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-7 text-xs w-[130px]"
              data-testid="user-activity-from-date"
              aria-label="From date"
            />
            <span className="text-slate-400">→</span>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-7 text-xs w-[130px]"
              data-testid="user-activity-to-date"
              aria-label="To date"
            />
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-slate-600"
                onClick={clearFilters}
                data-testid="user-activity-clear-filter-btn"
              >
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-between py-2">
          <span className="text-xs text-slate-500" data-testid="user-activity-count">
            {data ? (
              hasActiveFilter
                ? `${filteredEvents.length} of ${events.length} events shown (filtered)`
                : `${events.length} of ${data.total} events`
            ) : ''}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={fetchHistory} disabled={loading} data-testid="user-activity-refresh-btn">
              <RotateCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {allowJumpToFullLog && onJumpToFullLog && (
              <Button variant="outline" size="sm" onClick={onJumpToFullLog} data-testid="user-activity-jump-btn">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Full log
              </Button>
            )}
          </div>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : events.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">Nothing to show for this user.</div>
        ) : filteredEvents.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500" data-testid="user-activity-empty-filtered">
            No events match the current filter.
            <div className="mt-2">
              <Button variant="outline" size="sm" onClick={clearFilters}>Clear filter</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 pb-6" data-testid="user-activity-timeline">
            {dayGroups.map(([day, evs]) => (
              <div key={day}>
                <div className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-2 sticky top-0 bg-white py-1">
                  {day === 'Unknown' ? 'Unknown date' : new Date(day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
                <ol className="space-y-2 border-l border-slate-200 ml-1.5">
                  {evs.map((evt) => {
                    const src = SOURCE_LABEL[evt.source] || { label: evt.source, className: 'bg-slate-100 text-slate-600' };
                    const act = evt.actor || {};
                    return (
                      <li key={evt.id} className="relative pl-4" data-testid={`user-activity-evt-${evt.id}`}>
                        <span className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white bg-current ${CAT_DOT[evt.category] || 'text-slate-400'}`}></span>
                        <div className="flex items-start justify-between gap-2 mb-0.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-medium text-slate-900">{CAT_LABEL[evt.category] || evt.category}</span>
                            <Badge className={`${src.className} border text-[10px] py-0`}>{src.label}</Badge>
                          </div>
                          <span className="text-[11px] text-slate-400 whitespace-nowrap">{formatTs(evt.timestamp).split(', ')[1]}</span>
                        </div>
                        <EventBody evt={evt} />
                        <div className="text-[11px] text-slate-400 mt-1">
                          {act?.id && target?.id && act.id === target.id
                            ? 'by user (self)'
                            : act?.name
                              ? `by ${act.name}${act.indos_number ? ` (${act.indos_number})` : ''}`
                              : `by ${src.label}`}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default UserActivityDrawer;
